import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// Visor de documentos legales. MISMO archivo en el panel admin (vista previa
// del super_admin) y en el front de clientes (modal público): si se cambia
// uno, copiar el otro.
//
//  - Texto del editor: HTML ya saneado por el backend (sanitize-html) y por el
//    editor del panel; se muestra como hoja de papel.
//  - PDF: pdf.js (cargado bajo demanda desde cdnjs) dibuja cada página en un
//    <canvas>. Las páginas se reservan con su alto real y se dibujan solo al
//    acercarse a la vista, así un PDF de 80 páginas no congela el modal.
//  - Imagen (PNG/JPG/WEBP): se dibuja en un <canvas> con el mismo zoom.
// El archivo se descarga con fetch (o llega como Blob en la vista previa de
// un archivo aún sin guardar), nunca con <img>/<iframe> directos.

const PDFJS_VERSION = '3.11.174';
const PDFJS_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`;
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
// Techo de píxeles por canvas: evita agotar memoria en celulares al acercar.
const MAX_CANVAS_PIXELS = 16_000_000;

let pdfjsPromise: Promise<any> | null = null;
const loadPdfJs = (): Promise<any> => {
  const w = window as any;
  if (w.pdfjsLib) return Promise.resolve(w.pdfjsLib);
  pdfjsPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${PDFJS_BASE}/pdf.min.js`;
    script.async = true;
    script.onload = () => {
      const lib = w.pdfjsLib;
      if (!lib) { reject(new Error('pdf.js no disponible')); return; }
      lib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
      resolve(lib);
    };
    script.onerror = () => { pdfjsPromise = null; script.remove(); reject(new Error('No se pudo cargar pdf.js')); };
    document.head.appendChild(script);
  });
  return pdfjsPromise;
};

interface PageSlot {
  index: number;
  canvas: HTMLCanvasElement;
  baseWidth: number;
  baseHeight: number;
  renderedWidth: number;
  task: any;
}

@Component({
  selector: 'app-legal-document-viewer',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ldv" [class.is-file]="mode() === 'file'">
      <div class="ldv-toolbar" *ngIf="mode() === 'file' && state() === 'ready'" role="toolbar" aria-label="Controles del documento">
        <span class="ldv-pages" aria-live="polite"><i class="bi" [ngClass]="isPdf() ? 'bi-file-earmark-pdf' : 'bi-image'" aria-hidden="true"></i>
          {{ isPdf() ? 'Página ' + currentPage() + ' de ' + pageCount() : 'Imagen' }}</span>
        <div class="ldv-zoom">
          <button type="button" (click)="zoomBy(-1)" [disabled]="zoom() <= zoomSteps[0]" title="Alejar" aria-label="Alejar"><i class="bi bi-zoom-out"></i></button>
          <button type="button" class="ldv-zoom-value" (click)="setZoom(1)" title="Ajustar al ancho" aria-label="Ajustar al ancho">{{ zoom() * 100 | number:'1.0-0' }}%</button>
          <button type="button" (click)="zoomBy(1)" [disabled]="zoom() >= zoomSteps[zoomSteps.length - 1]" title="Acercar" aria-label="Acercar"><i class="bi bi-zoom-in"></i></button>
        </div>
      </div>

      <div class="ldv-stage" #stage (scroll)="onScroll()">
        <article class="ldv-paper ldv-html" *ngIf="mode() === 'html'" [innerHTML]="safeHtml()"></article>
        <!-- Se muestra/oculta desde el código (no con binding): las medidas de
             las páginas deben estar disponibles en cuanto termina la carga. -->
        <div class="ldv-pages-host" #pagesHost hidden></div>

        <div class="ldv-status" *ngIf="state() === 'loading'"><span class="ldv-spinner" aria-hidden="true"></span><span>Cargando documento…</span></div>
        <div class="ldv-status ldv-error" *ngIf="state() === 'error'" role="alert">
          <i class="bi bi-exclamation-triangle" aria-hidden="true"></i><span>{{ errorText() }}</span>
          <button type="button" (click)="reload()"><i class="bi bi-arrow-clockwise"></i> Reintentar</button>
        </div>
        <div class="ldv-status" *ngIf="state() === 'empty'"><i class="bi bi-file-earmark" aria-hidden="true"></i><span>{{ emptyText }}</span></div>
      </div>
    </div>
  `,
  styles: [`
    :host{display:block;min-height:0;--ldv-stage:#e9eef5;--ldv-paper:#fff;--ldv-ink:#17243a;--ldv-muted:#4b5d78;--ldv-accent:#1d4ed8;--ldv-line:#d6dfeb;--ldv-bar:#f8fafc}
    .ldv{display:flex;flex-direction:column;height:100%;min-height:0;background:var(--ldv-stage)}
    .ldv-toolbar{display:flex;align-items:center;gap:.6rem;padding:.5rem .75rem;border-bottom:1px solid var(--ldv-line);background:var(--ldv-bar);color:var(--ldv-ink);font-size:.8rem}
    .ldv-pages{display:inline-flex;align-items:center;gap:.4rem;font-weight:700;white-space:nowrap}
    .ldv-pages i{color:var(--ldv-accent)}
    .ldv-zoom{display:inline-flex;align-items:center;gap:.2rem;margin-left:auto;padding:2px;border:1px solid var(--ldv-line);border-radius:10px;background:#fff}
    .ldv-zoom button{display:grid;place-items:center;min-width:32px;height:30px;padding:0 .4rem;color:var(--ldv-ink);border:0;border-radius:8px;background:transparent;font:inherit;font-weight:700;cursor:pointer}
    .ldv-zoom button:hover:not(:disabled){background:#e2e8f0}
    .ldv-zoom button:disabled{opacity:.35;cursor:not-allowed}
    .ldv-zoom-value{min-width:56px!important;font-variant-numeric:tabular-nums}
    .ldv-stage{position:relative;flex:1 1 auto;min-height:0;overflow:auto;padding:18px 14px 24px;overscroll-behavior:contain;-webkit-overflow-scrolling:touch}
    .ldv-paper{box-sizing:border-box;width:min(780px,100%);margin:0 auto;padding:40px 48px;color:var(--ldv-ink);border:1px solid var(--ldv-line);border-radius:8px;background:var(--ldv-paper);box-shadow:0 2px 5px rgba(15,23,42,.1),0 18px 38px -24px rgba(15,23,42,.55);font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.7;overflow-wrap:anywhere}
    .ldv-html ::ng-deep h1{margin:0 0 14px;font-size:26px;line-height:1.25;color:var(--ldv-ink)}
    .ldv-html ::ng-deep h2{margin:22px 0 10px;font-size:21px;line-height:1.3;color:var(--ldv-ink)}
    .ldv-html ::ng-deep h3{margin:20px 0 8px;font-size:18px;color:var(--ldv-ink)}
    .ldv-html ::ng-deep h4{margin:16px 0 6px;font-size:15.5px;color:var(--ldv-ink)}
    .ldv-html ::ng-deep :is(h1,h2,h3,h4):first-child{margin-top:0}
    .ldv-html ::ng-deep p{margin:0 0 12px;color:var(--ldv-ink)}
    .ldv-html ::ng-deep :is(ul,ol){margin:0 0 12px;padding-left:26px}
    .ldv-html ::ng-deep li{margin:0 0 4px;color:var(--ldv-ink)}
    .ldv-html ::ng-deep blockquote{margin:12px 0;padding:10px 16px;border-left:3px solid var(--ldv-accent);border-radius:0 6px 6px 0;background:#eff6ff}
    .ldv-html ::ng-deep hr{margin:20px 0;border:0;border-top:1px solid var(--ldv-line)}
    .ldv-html ::ng-deep a{color:var(--ldv-accent);text-decoration:underline}
    /* La hoja es siempre blanca: se blinda el color frente a reglas globales
       con !important del sitio anfitrión (p. ej. .superadmin-container p/h3/strong). */
    .ldv-html ::ng-deep :is(h1,h2,h3,h4,p,li,strong,b,em,i,u,s,blockquote,div,span,small){color:var(--ldv-ink)!important}
    .ldv-html ::ng-deep a{color:var(--ldv-accent)!important}
    .ldv-toolbar :is(span,strong,small){color:var(--ldv-ink)!important}
    .ldv-pages-host{display:grid;justify-items:center;gap:16px}
    .ldv-pages-host[hidden]{display:none}
    .ldv-pages-host ::ng-deep canvas{display:block;max-width:none;border-radius:4px;background:#fff;box-shadow:0 2px 6px rgba(15,23,42,.14),0 16px 34px -22px rgba(15,23,42,.6)}
    .ldv-status{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.7rem;min-height:240px;padding:2rem 1rem;color:var(--ldv-muted);font-size:.9rem;text-align:center}
    .ldv-status span{color:inherit!important}
    .ldv-status i{font-size:1.6rem}
    .ldv-error{color:#b42318}
    .ldv-error button{display:inline-flex;align-items:center;gap:.35rem;height:36px;padding:0 .9rem;color:var(--ldv-ink);border:1px solid var(--ldv-line);border-radius:10px;background:#fff;font:inherit;font-weight:700;cursor:pointer}
    .ldv-spinner{width:28px;height:28px;border:3px solid rgba(29,78,216,.22);border-top-color:var(--ldv-accent);border-radius:50%;animation:ldv-spin .8s linear infinite}
    @keyframes ldv-spin{to{transform:rotate(360deg)}}
    @media (max-width:640px){
      .ldv-stage{padding:10px 8px 18px}
      .ldv-paper{padding:24px 18px;font-size:14.5px}
      .ldv-toolbar{gap:.4rem;padding:.45rem .55rem}
    }
  `],
})
export class LegalDocumentViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** HTML del editor (si no hay archivo). */
  @Input() html = '';
  /** URL absoluta del archivo publicado. */
  @Input() fileUrl: string | null = null;
  /** Archivo local aún sin guardar (vista previa del panel). Tiene prioridad sobre fileUrl. */
  @Input() fileBlob: Blob | null = null;
  @Input() mime = '';
  @Input() fileName = '';
  @Input() emptyText = 'Este documento aún no tiene contenido.';

  @ViewChild('stage', { static: true }) private stageRef!: ElementRef<HTMLDivElement>;
  @ViewChild('pagesHost', { static: true }) private pagesHostRef!: ElementRef<HTMLDivElement>;

  private readonly sanitizer = inject(DomSanitizer);
  private readonly zone = inject(NgZone);

  readonly zoomSteps = ZOOM_STEPS;
  readonly mode = signal<'html' | 'file' | 'none'>('none');
  readonly state = signal<'loading' | 'ready' | 'error' | 'empty'>('empty');
  readonly errorText = signal('');
  readonly safeHtml = signal<SafeHtml>('');
  readonly zoom = signal(1);
  readonly pageCount = signal(0);
  readonly currentPage = signal(1);
  readonly isPdf = signal(false);

  private viewReady = false;
  private loadToken = 0;
  private pdfDoc: any = null;
  private image: ImageBitmap | HTMLImageElement | null = null;
  private slots: PageSlot[] = [];
  private observer: IntersectionObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private lastWidth = 0;
  private relayoutTimer: ReturnType<typeof setTimeout> | null = null;
  private objectUrl: string | null = null;

  ngOnChanges(): void {
    if (this.viewReady) this.load();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.stageWidth();
      if (Math.abs(width - this.lastWidth) < 4) return;
      this.lastWidth = width;
      if (this.relayoutTimer) clearTimeout(this.relayoutTimer);
      this.relayoutTimer = setTimeout(() => this.zone.run(() => this.layoutPages()), 120);
    });
    this.resizeObserver.observe(this.stageRef.nativeElement);
    this.load();
  }

  ngOnDestroy(): void {
    this.loadToken++;
    this.teardown();
    this.resizeObserver?.disconnect();
    if (this.relayoutTimer) clearTimeout(this.relayoutTimer);
  }

  reload(): void {
    this.load();
  }

  setZoom(value: number): void {
    if (value === this.zoom()) return;
    const stage = this.stageRef.nativeElement;
    const ratio = stage.scrollHeight ? stage.scrollTop / stage.scrollHeight : 0;
    this.zoom.set(value);
    this.layoutPages();
    stage.scrollTop = ratio * stage.scrollHeight;
  }

  zoomBy(direction: 1 | -1): void {
    const index = ZOOM_STEPS.indexOf(this.zoom());
    const next = ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, (index < 0 ? 2 : index) + direction))];
    this.setZoom(next);
  }

  onScroll(): void {
    if (!this.isPdf() || !this.slots.length) return;
    const stage = this.stageRef.nativeElement;
    const middle = stage.scrollTop + stage.clientHeight / 3;
    let page = 1;
    for (const slot of this.slots) {
      if (slot.canvas.offsetTop <= middle) page = slot.index + 1;
      else break;
    }
    if (page !== this.currentPage()) this.currentPage.set(page);
  }

  private stageWidth(): number {
    const stage = this.stageRef.nativeElement;
    const style = getComputedStyle(stage);
    return Math.max(200, stage.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
  }

  private teardown(): void {
    this.observer?.disconnect();
    this.observer = null;
    for (const slot of this.slots) slot.task?.cancel?.();
    this.slots = [];
    this.pagesHostRef.nativeElement.replaceChildren();
    this.pagesHostRef.nativeElement.hidden = true;
    this.pdfDoc?.destroy?.();
    this.pdfDoc = null;
    if (this.image && 'close' in this.image) this.image.close();
    this.image = null;
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
  }

  private async load(): Promise<void> {
    const token = ++this.loadToken;
    this.teardown();
    this.zoom.set(1);
    this.currentPage.set(1);
    this.pageCount.set(0);
    this.stageRef.nativeElement.scrollTop = 0;

    if (!this.fileBlob && !this.fileUrl) {
      const hasHtml = !!this.html && !!this.html.replace(/<[^>]*>|&nbsp;|\s/g, '');
      this.mode.set(hasHtml ? 'html' : 'none');
      // Contenido saneado en el backend (sanitize-html) y en el editor del panel.
      this.safeHtml.set(hasHtml ? this.sanitizer.bypassSecurityTrustHtml(this.html) : '');
      this.state.set(hasHtml ? 'ready' : 'empty');
      return;
    }

    this.mode.set('file');
    this.state.set('loading');
    try {
      const blob = this.fileBlob ?? await this.fetchFile(this.fileUrl!);
      if (token !== this.loadToken) return;
      const type = (this.mime || blob.type || '').toLowerCase();
      this.objectUrl = URL.createObjectURL(blob);
      this.lastWidth = this.stageWidth();
      if (type === 'application/pdf') {
        this.isPdf.set(true);
        const pdfjs = await loadPdfJs();
        const data = new Uint8Array(await blob.arrayBuffer());
        const doc = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;
        if (token !== this.loadToken) { doc.destroy(); return; }
        this.pdfDoc = doc;
        await this.buildPdfSlots(token);
      } else if (type.startsWith('image/')) {
        this.isPdf.set(false);
        this.image = await this.decodeImage(blob);
        if (token !== this.loadToken) return;
        this.slots = [this.makeSlot(0, this.image.width, this.image.height)];
      } else {
        throw new Error('unsupported');
      }
      this.pageCount.set(this.slots.length || 1);
      this.pagesHostRef.nativeElement.hidden = false;
      this.state.set('ready');
      this.layoutPages();
    } catch (error) {
      if (token !== this.loadToken) return;
      this.errorText.set((error as Error)?.message === 'unsupported'
        ? 'Este formato de archivo no se puede mostrar.'
        : 'No fue posible cargar el documento. Revisa tu conexión e inténtalo de nuevo.');
      this.state.set('error');
    }
  }

  private async fetchFile(url: string): Promise<Blob> {
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  }

  private async decodeImage(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
    if ('createImageBitmap' in window) {
      try { return await createImageBitmap(blob); } catch { /* Safari antiguo: respaldo con <img> */ }
    }
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image'));
      img.src = this.objectUrl!;
    });
  }

  private makeSlot(index: number, baseWidth: number, baseHeight: number): PageSlot {
    const canvas = document.createElement('canvas');
    canvas.dataset['page'] = String(index);
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', this.isPdf() ? `Página ${index + 1}` : (this.fileName || 'Documento'));
    this.pagesHostRef.nativeElement.appendChild(canvas);
    return { index, canvas, baseWidth, baseHeight, renderedWidth: 0, task: null };
  }

  private async buildPdfSlots(token: number): Promise<void> {
    // Solo se leen las dimensiones (rápido); el dibujo es perezoso.
    for (let i = 0; i < this.pdfDoc.numPages; i++) {
      const page = await this.pdfDoc.getPage(i + 1);
      if (token !== this.loadToken) return;
      const viewport = page.getViewport({ scale: 1 });
      this.slots.push(this.makeSlot(i, viewport.width, viewport.height));
    }
    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const slot = this.slots[Number((entry.target as HTMLElement).dataset['page'])];
        if (slot) this.renderSlot(slot);
      }
    }, { root: this.stageRef.nativeElement, rootMargin: '600px 0px' });
    for (const slot of this.slots) this.observer.observe(slot.canvas);
  }

  /** Ancho en CSS de cada página: ajustada al ancho disponible × zoom. */
  private cssWidthFor(slot: PageSlot): number {
    const fit = Math.min(this.stageWidth(), this.isPdf() ? 900 : Math.max(slot.baseWidth, 320));
    return Math.round(fit * this.zoom());
  }

  private layoutPages(): void {
    if (this.state() !== 'ready') return;
    for (const slot of this.slots) {
      const width = this.cssWidthFor(slot);
      slot.canvas.style.width = `${width}px`;
      slot.canvas.style.height = `${Math.round(width * slot.baseHeight / slot.baseWidth)}px`;
      if (!this.isPdf()) this.renderSlot(slot);
      else if (slot.renderedWidth && slot.renderedWidth !== width) {
        slot.renderedWidth = 0;
        if (this.isNearView(slot.canvas)) this.renderSlot(slot);
      }
    }
    this.onScroll();
  }

  private isNearView(el: HTMLElement): boolean {
    const stage = this.stageRef.nativeElement;
    const top = el.offsetTop - stage.scrollTop;
    return top < stage.clientHeight + 600 && top + el.offsetHeight > -600;
  }

  private renderSlot(slot: PageSlot): void {
    const cssWidth = this.cssWidthFor(slot);
    if (slot.renderedWidth === cssWidth) return;
    slot.renderedWidth = cssWidth;
    const cssHeight = cssWidth * slot.baseHeight / slot.baseWidth;
    let ratio = window.devicePixelRatio || 1;
    if (cssWidth * cssHeight * ratio * ratio > MAX_CANVAS_PIXELS) ratio = Math.sqrt(MAX_CANVAS_PIXELS / (cssWidth * cssHeight));
    const pixelWidth = Math.max(1, Math.floor(cssWidth * ratio));
    const pixelHeight = Math.max(1, Math.floor(cssHeight * ratio));

    if (!this.isPdf() && this.image) {
      const canvas = slot.canvas;
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.image as CanvasImageSource, 0, 0, pixelWidth, pixelHeight);
      return;
    }

    slot.task?.cancel?.();
    const doc = this.pdfDoc;
    this.zone.runOutsideAngular(async () => {
      try {
        const page = await doc.getPage(slot.index + 1);
        const viewport = page.getViewport({ scale: pixelWidth / slot.baseWidth });
        // Se dibuja en un canvas aparte y se copia al final: la página
        // visible no queda en blanco mientras se redibuja con otro zoom.
        const buffer = document.createElement('canvas');
        buffer.width = Math.floor(viewport.width);
        buffer.height = Math.floor(viewport.height);
        const ctx = buffer.getContext('2d');
        if (!ctx) return;
        slot.task = page.render({ canvasContext: ctx, viewport });
        await slot.task.promise;
        if (doc !== this.pdfDoc) return;
        slot.canvas.width = buffer.width;
        slot.canvas.height = buffer.height;
        slot.canvas.getContext('2d')?.drawImage(buffer, 0, 0);
      } catch (error: any) {
        if (error?.name !== 'RenderingCancelledException') slot.renderedWidth = 0;
      }
    });
  }
}
